import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  Avatar,
  IconButton,
  Button,
  Typography,
  Skeleton,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Box,
} from '@mui/material';
import { PlayArrow as PlayArrowIcon, ImageOutlined as ImageIcon, Add as AddIcon, Tune as TuneIcon } from '@mui/icons-material';
import { Action, SignedUrlInfo, SEND_ACTION_DEFAULTS } from '../types/action';
import { SendCurrentActionRequest } from '../types/currentAction';
import { actionsService } from '../services/actionsService';
import { currentActionService } from '../services/currentActionService';
import { ActionEditor } from './ActionEditor';
import { useTranslation } from 'react-i18next';

interface ActionSelectorProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  actions: Action[];
  onActionSelected: (actionId: string) => void;
  onReloadActions?: () => void;
  /**
   * A sent schedule is on the tablet and a send replaces it (BA-85 D1): quick-send then
   * opens the replace confirm through onActionSelected instead of sending at once
   */
  replacing?: boolean;
}

interface ActionListItemProps {
  action: Action;
  imageUrl: string | null;
  imageLoading: boolean;
  sendingActionId: string | null;
  onQuickSend: () => void;
  onSettingsClick: () => void;
}

const ActionListItem: React.FC<ActionListItemProps> = ({ action, imageUrl, imageLoading, sendingActionId, onQuickSend, onSettingsClick }) => {
  const { t } = useTranslation();
  const isSending = sendingActionId === action.id;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
        {action.name}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        {/* Image */}
        {imageLoading ? (
          <Skeleton variant="rounded" width={120} height={120} sx={{ flexShrink: 0 }} />
        ) : !imageUrl ? (
          <Avatar
            variant="rounded"
            sx={{
              width: 120,
              height: 120,
              flexShrink: 0,
              bgcolor: 'action.hover',
            }}
          >
            <ImageIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Avatar>
        ) : (
          <Box
            component="img"
            src={imageUrl}
            alt={action.name}
            sx={{
              width: 120,
              height: 120,
              flexShrink: 0,
              borderRadius: 1,
              objectFit: 'cover',
            }}
          />
        )}
        {/* Buttons stacked vertically */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Tooltip title={t('actionSelector.sendWithSettings')}>
            <IconButton
              color="primary"
              onClick={onSettingsClick}
              disabled={isSending}
              sx={{
                border: '1px solid',
                borderColor: 'primary.main',
                width: 48,
                height: 48,
              }}
            >
              <TuneIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('actionSelector.quickSend')}>
            <IconButton
              color="primary"
              onClick={onQuickSend}
              disabled={isSending}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                width: 48,
                height: 48,
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&.Mui-disabled': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  opacity: 0.7,
                },
              }}
            >
              {isSending ? <CircularProgress size={24} color="inherit" /> : <PlayArrowIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export const ActionSelector: React.FC<ActionSelectorProps> = ({
  open,
  onClose,
  deviceId,
  actions,
  onActionSelected,
  onReloadActions,
  replacing = false,
}) => {
  const { t } = useTranslation();
  const [editorOpen, setEditorOpen] = useState(false);
  const [signedUrls, setSignedUrls] = useState<Record<string, SignedUrlInfo>>({});
  const [urlsLoading, setUrlsLoading] = useState(false);
  const [sendingActionId, setSendingActionId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // Batch fetch signed URLs when dialog opens
  useEffect(() => {
    if (!open) {
      return;
    }

    const actionIdsWithPictures = actions
      .filter(a => a.thumbnailUrl || a.pictureUrl)
      .map(a => a.id);

    if (actionIdsWithPictures.length === 0) {
      setUrlsLoading(false);
      return;
    }

    const fetchBatchUrls = async () => {
      setUrlsLoading(true);
      try {
        const response = await actionsService.getBatchSignedPictureUrls(
          deviceId,
          actionIdsWithPictures,
          true // prefer thumbnails
        );
        setSignedUrls(response.urls);
      } catch (err) {
        console.error('[ActionSelector] Error fetching batch picture URLs:', err);
      } finally {
        setUrlsLoading(false);
      }
    };

    fetchBatchUrls();
  }, [open, actions, deviceId]);

  const handleQuickSend = async (action: Action) => {
    if (replacing) {
      onActionSelected(action.id);
      return;
    }
    setSendingActionId(action.id);
    try {
      const d = action.defaults;
      const request: SendCurrentActionRequest = {
        actionId: action.id,
        timeToStart: d?.timeToStart ?? SEND_ACTION_DEFAULTS.timeToStart,
        stayUntilParentApproves: d?.stayUntilParentApproves ?? SEND_ACTION_DEFAULTS.stayUntilParentApproves,
        stayUntilChecked: d?.stayUntilChecked ?? SEND_ACTION_DEFAULTS.stayUntilChecked,
        duration: (d?.stayUntilFixedTime ?? SEND_ACTION_DEFAULTS.stayUntilFixedTime)
          ? (d?.duration ?? SEND_ACTION_DEFAULTS.duration)
          : 0,
      };
      await currentActionService.sendAction(deviceId, request);
      setSnackbar({ message: t('actionSelector.quickSendSuccess'), severity: 'success' });
      setTimeout(() => onClose(), 500);
    } catch (error) {
      console.error('Failed to quick-send action:', error);
      setSnackbar({ message: t('actionSelector.quickSendFailed'), severity: 'error' });
    } finally {
      setSendingActionId(null);
    }
  };

  const handleSettingsClick = (actionId: string) => {
    onActionSelected(actionId);
  };

  const handleAddAction = () => {
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
  };

  const handleEditorSave = () => {
    // Trigger reload of actions in parent component
    if (onReloadActions) {
      onReloadActions();
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('actionSelector.title')}</DialogTitle>
      <DialogContent>
        {actions.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            {t('actionSelector.noActions')}
          </Typography>
        ) : (
          <List>
            {actions.map((action) => {
              const signedUrl = signedUrls[action.id];
              const hasImage = !!(action.thumbnailUrl || action.pictureUrl);
              return (
                <ActionListItem
                  key={action.id}
                  action={action}
                  imageUrl={signedUrl?.url || null}
                  imageLoading={hasImage && urlsLoading}
                  sendingActionId={sendingActionId}
                  onQuickSend={() => handleQuickSend(action)}
                  onSettingsClick={() => handleSettingsClick(action.id)}
                />
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleAddAction} startIcon={<AddIcon />} variant="outlined">
          {t('actions.addAction')}
        </Button>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>

    {/* ActionEditor for creating new actions */}
    <ActionEditor
      open={editorOpen}
      onClose={handleEditorClose}
      onSave={handleEditorSave}
      deviceId={deviceId}
      action={null}
    />

    {/* Snackbar for quick send feedback */}
    <Snackbar
      open={!!snackbar}
      autoHideDuration={3000}
      onClose={() => setSnackbar(null)}
    >
      <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)}>
        {snackbar?.message}
      </Alert>
    </Snackbar>
    </>
  );
};
